import { apiEndpoint } from '../config/api';
import { apiFetch } from './api';

async function parseResponse(response) {
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(body.message || `Request failed with status ${response.status}`);
  }
  return body;
}

// GET /class-audit-pull -- one server-resolved row per eligible class for a tracking year.
// params (snake_case to match the endpoint's query contract):
//   trackingyear   (REQUIRED -- the backend returns 400 without it),
//   trackingseason, com_code, facility_name, mapping_source, ambiguous, search (all optional).
// Returns { rows, row_count, truncated, max_rows }. When truncated is true the caller must
// prompt the auditor to narrow filters (results are incomplete).
//
// This allowlist is EXHAUSTIVE and silent: a param missing from it is dropped without error, so the
// caller believes it filtered and receives an unfiltered (and probably truncated) page. Any new
// server-side filter must be added HERE as well as in the endpoint and the caller.
export async function getClassAuditRows(params = {}) {
  const qs = new URLSearchParams();
  if (params.trackingyear != null) qs.set('trackingyear', params.trackingyear);
  if (params.trackingseason != null) qs.set('trackingseason', params.trackingseason);
  if (params.com_code != null) qs.set('com_code', params.com_code);
  if (params.facility_name != null) qs.set('facility_name', params.facility_name);
  if (params.mapping_source != null) qs.set('mapping_source', params.mapping_source);
  if (params.ambiguous != null) qs.set('ambiguous', params.ambiguous);
  if (params.search != null) qs.set('search', params.search);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const response = await apiFetch(`${apiEndpoint}/class-audit-pull${suffix}`);
  return parseResponse(response);
}

// POST /class-rule-preview -- production-equivalent preview of a PROPOSED mapping rule.
// payload: { team_name_pattern, level_id, priority?, com_code?, trackingyear? }. Read-only:
// nothing is written and no build is triggered. Returns { candidate, safety, truncated, counts,
// affected_franchises, affected_seasons, samples }. safety is 'safe' | 'warning' | 'blocked'
// (blocked = the rule creates a same-rank, different-level conflict). Throws on non-2xx (e.g. 400
// invalid level/priority/pattern).
export async function previewClassRule(payload) {
  const response = await apiFetch(`${apiEndpoint}/class-rule-preview`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parseResponse(response);
}

// GET /class-canonical-levels -- the assignable canonical level catalog (active levels,
// display-ordered). Returns { levels: [...] }. Read-only; warehouse-owned.
export async function getClassCanonicalLevels() {
  const response = await apiFetch(`${apiEndpoint}/class-canonical-levels`);
  return parseResponse(response);
}

// ---- reusable rule LIFECYCLE (app.lksi_team_level_mapping_rules) --------------------------------
// A saved rule is authoritative in the app table IMMEDIATELY; Class Audit reflects it only after the
// next dbt build, so every write returns warehouse_refresh_pending. The server re-runs the
// production-equivalent preview before committing every create/edit, and refuses a same-rank
// conflict (409) or a pattern that matches no class (400).

// GET /class-rules?com_code=&source=&is_active= -- list rules joined to their level plus warehouse
// build info; each rule carries pending_rebuild. Read-only.
export async function listClassRules(params = {}) {
  const qs = new URLSearchParams();
  if (params.com_code != null && params.com_code !== '') qs.set('com_code', params.com_code);
  if (params.source != null && params.source !== '') qs.set('source', params.source);
  if (params.is_active != null && params.is_active !== '') qs.set('is_active', params.is_active);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const response = await apiFetch(`${apiEndpoint}/class-rules${suffix}`);
  return parseResponse(response);
}

// POST /class-rule-publish -- create a reusable rule. payload: { team_name_pattern, level_id,
// priority?, com_code?, trackingyear? }. created_by is the authenticated user (server-derived).
// Returns { rule, preview, warehouse_refresh_pending }.
export async function publishClassRule(payload) {
  const response = await apiFetch(`${apiEndpoint}/class-rule-publish`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  });
  return parseResponse(response);
}

// POST /class-rule-update -- edit a rule in place. payload adds { rule_id }. source/created_by are
// preserved (immutable provenance); updated_by/at record the steward. Returns { rule, preview, ... }.
export async function updateClassRule(payload) {
  const response = await apiFetch(`${apiEndpoint}/class-rule-update`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  });
  return parseResponse(response);
}

// POST /class-rule-removal-preview -- READ-ONLY impact preview for deactivating a rule. payload:
// { rule_id }. Runs the same removal preview class-rule-deactivate runs, so the confirmation dialog
// can show the consequences BEFORE the steward commits. Returns { rule, preview, no_op }; preview is
// null (no_op true) when the rule is already inactive. Writes nothing, triggers no build.
export async function previewClassRuleRemoval(ruleId) {
  const response = await apiFetch(`${apiEndpoint}/class-rule-removal-preview`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ rule_id: ruleId }),
  });
  return parseResponse(response);
}

// POST /class-rule-deactivate -- deactivate a rule (any origin, baseline included). payload:
// { rule_id }. Returns { rule, preview (removal impact), warehouse_refresh_pending }.
export async function deactivateClassRule(ruleId) {
  const response = await apiFetch(`${apiEndpoint}/class-rule-deactivate`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ rule_id: ruleId }),
  });
  return parseResponse(response);
}

// POST /class-rule-reactivate -- reactivate a previously deactivated rule. payload: { rule_id }.
// Re-previewed as an add; a now-conflict or active collision returns 409.
export async function reactivateClassRule(ruleId) {
  const response = await apiFetch(`${apiEndpoint}/class-rule-reactivate`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ rule_id: ruleId }),
  });
  return parseResponse(response);
}

// POST /class-rule-delete -- permanently delete a Season Manager-authored rule. payload: { rule_id }.
// Server enforces: source='season_manager', inactive, and pending_rebuild cleared. Baseline rules and
// active/pending rules are refused (403/409). No rebuild needed. Returns { deleted, warehouse_refresh_pending }.
export async function deleteClassRule(ruleId) {
  const response = await apiFetch(`${apiEndpoint}/class-rule-delete`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ rule_id: ruleId }),
  });
  return parseResponse(response);
}

// POST /class-level-assign -- create/replace the active manual level for one class.
// payload: { team_dim_id, level_id, override_reason? }. reviewed_by is derived server-side
// from the authenticated user (never sent from the client). Returns the resolved class
// preview. Throws on non-2xx -- e.g. 400 (program-type mismatch, or override_reason required
// when changing an auto-suggested level), 409 (concurrent modification, retry).
export async function assignClassLevel(payload) {
  const response = await apiFetch(`${apiEndpoint}/class-level-assign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parseResponse(response);
}

// DELETE /class-level-remove/{team_dim_id} -- soft-deactivate the active manual level.
// Effective level falls back to the automated suggestion, or to unassigned when there is
// none. Idempotent. Returns the resolved class preview.
export async function removeClassLevel(teamDimId) {
  const response = await apiFetch(
    `${apiEndpoint}/class-level-remove/${encodeURIComponent(teamDimId)}`,
    { method: 'DELETE' }
  );
  return parseResponse(response);
}
